<script setup>
/**
 * One task in the list: what it is, when it starts, whether it is in use, and the way in.
 *
 * ONE ROW GRAMMAR, the same one `AttentionRow` sets and for the same reason:
 * **[mark] [what · when it starts · meta] [state + switch] [go]**. The trailing cluster is
 * the same slot in the same order on every row, so the eye finds the control at the same
 * place whatever the row says and whichever direction its text runs.
 *
 * THE SWITCH IS ON THE ROW, not only inside the editor, and that is the one real decision
 * in this file. Turning a task off is the thing an owner does in a hurry — a delivery task
 * that is promising a day she cannot keep during Ramadan, a booking task while the branch
 * is closed — and making her open an editor, scroll to a section and press Save to do it
 * is three screens of latency in front of a live customer conversation. It is safe here
 * only because the write is a genuine partial patch (`api/client.js` `updateTask`): the
 * row sends `{ enabled }` and nothing else, so it cannot overwrite a draft somebody has
 * open in another tab. That property is the whole reason the BFF is a PATCH.
 *
 * WHAT THIS ROW DOES NOT SAY: how many steps the task has. The BFF's list projection does
 * not carry them — the sidecar's list genuinely has no `steps` — and the two ways to put
 * "4 steps" here are to fetch every task's detail on the section's first screen or to
 * print a 0 that is a lie for a task with four. `routes/api/scenarios.ts` writes the same
 * refusal down from the other side.
 *
 * ── THE LINKS UNDER THE ROW, AND WHY THEY ARE NOT DECORATION ───────────────────────
 *
 * Before this change, `grep frontendURL` across `pages/` and `components/` returned exactly
 * one hit per screen — the screen's own — and NOT ONE LINK between Tasks, Menus, Forms,
 * Approved messages and Connections. Five objects that only make sense together, and no way
 * to get from any of them to any other except back through the sidebar. A blind reader,
 * given the composer with no history, put the cost of that plainly: *"None of the five step
 * names is where I connect my calendar. Checking whether I'm free on Thursday is the whole
 * point of booking."* The hard part of a task is the thing it reaches OUT to, and nothing
 * on any screen reached out to it.
 *
 * So each row carries the things this task uses, as real `router-link`s:
 *
 *   • THE WORDS ON THE CARD (`?step=arabic`) — a sub-destination of the composer. Named
 *     second by that same reader, above everything else on the old screen: *"I run a shop
 *     in Kuwait and half my customers write Arabic."*
 *   • THE SYSTEMS IT USES, or — and this is the one that matters — **Connect a system**
 *     when it uses none, straight into the connection editor with `?return=` so the owner
 *     lands back on this task instead of eight screens away. That query contract is the
 *     connection editor's own (`ConnectionEditorPage.vue`), which validates the name
 *     against `router.hasRoute()` before it will use it.
 *   • MAKE IT A FORM → the Forms tab of Approved messages, which is the only place a
 *     task's form is previewed and published.
 *   • START IT FROM A MENU → the Menus tab of this very page. That link is what replaces
 *     the deleted nav row: a menu option is how a customer starts a task by tapping instead
 *     of typing, and with the row gone there has to be something that says so from here.
 *
 * THE CARD WORDS AND THE FORM ARE GATED ON `hasGraph`, and the other two are not. Both of
 * those read the step graph — the Arabic screen lists a step per row, a form is built out of
 * the steps — so on a task with none they would open a screen with nothing on it. A
 * connection and a menu option are not like that: a task written as free text can call a
 * system and can be named by a menu row, so those two links are shown for every task.
 *
 * WHAT IS DELIBERATELY NOT HERE: a count of how many Arabic words are still missing. The
 * design asked for `عربي (n missing)`; the number cannot be computed from this row. It
 * needs the step GRAPH (`helpers/task-arabic.js` `cardWords` takes a definition), the list
 * projection carries none by the rule three paragraphs up, and the alternatives are one
 * detail fetch per row on the section's first screen or a number invented from an absence.
 * The link goes to the screen that already shows the true count instead.
 */
import { computed } from 'vue';

import Switch from 'dashboard/components-next/switch/Switch.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import ClaudeChip from './ClaudeChip.vue';
import TaskRemove from './task/TaskRemove.vue';
import { useWasloI18n } from '../i18n.js';
import { MARKER, isolateParts } from '../helpers/bidi.js';
import { relativeTime } from '../helpers/relative-time.js';
import { toIntlLocale } from '../helpers/intl.js';
import { CHANNEL_NAME_KEY, channelsNotOn } from '../helpers/task-channels.js';

const props = defineProps({
  /** A row from `wasloApi.getTasks`. */
  task: { type: Object, required: true },
  accountId: { type: Number, default: null },
  /** True while this row's own switch is being written. */
  isSaving: { type: Boolean, default: false },
  /**
   * Bumped by the parent when a write fails. `Switch` keeps a local copy of its model and
   * flips on click whether or not the parent agrees, so remounting it is the only reliable
   * way to snap it back to what the server actually says — the same fix, for the same
   * reason, as the Overview's reply switch.
   */
  switchEpoch: { type: Number, default: 0 },
  /**
   * P-10.26: the account's channels (`api/channels.js`). A task limited to some of them says
   * which it is not on; `[]` (not known) says nothing.
   */
  accountChannels: { type: Array, default: () => [] },
  /** E2 (P-8.4): Claude drafted it and nobody has edited it or switched it on since. */
  suggestedByClaude: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'removed']);

const { t, locale } = useWasloI18n();

/**
 * The trigger is the owner's own text inside a translated frame, so it goes through a real
 * bidi isolate rather than an interpolated string: an Arabic trigger in an English sentence
 * otherwise swallows the full stop that belongs to the frame and moves it.
 */
const startsWhen = computed(() =>
  isolateParts(
    t('WASLO.TASKS.LIST.STARTS_WHEN', { trigger: MARKER }),
    props.task.trigger
  )
);

const notOn = computed(() =>
  channelsNotOn(props.task.channels, props.accountChannels).map(kind => ({
    kind,
    label: t('WASLO.TASKS.LIST.NOT_ON', {
      channel: t(CHANNEL_NAME_KEY[kind]),
    }),
  }))
);

const connectionsLabel = computed(() =>
  props.task.connectionCount > 0
    ? t('WASLO.TASKS.LIST.CONNECTIONS', {
        count: new Intl.NumberFormat(toIntlLocale(locale.value)).format(
          props.task.connectionCount
        ),
      })
    : ''
);

/**
 * P-5.9: chats that reached this task in the last 7 days, and how many finished it. Absent
 * (not counted) and `null` (the count failed) draw nothing, never a zero.
 */
const usageLine = computed(() => {
  const usage = props.task.usage7d;
  if (!usage) return '';
  const format = value =>
    new Intl.NumberFormat(toIntlLocale(locale.value)).format(value);
  const reached = t(
    'WASLO.TASKS.LIST.USAGE_REACHED',
    { count: format(usage.conversations) },
    usage.conversations
  );
  if (usage.conversations === 0) return reached;
  const finished = t(
    'WASLO.TASKS.LIST.USAGE_FINISHED',
    { count: format(usage.finished) },
    usage.finished
  );
  return `${reached} ${finished}`;
});

const changedLabel = computed(() => {
  const when = relativeTime(props.task.updatedAt, locale.value, {
    justNow: t('WASLO.TASKS.LIST.UPDATED_JUST_NOW'),
  });
  return when ? t('WASLO.TASKS.LIST.UPDATED', { when }) : '';
});

/**
 * What is left under the trigger once the systems count became a LINK.
 *
 * It used to join two facts — the connection count and the last change — with a separator,
 * and the note for doing it in one string still stands for the day a second one comes back:
 * as two `<span>`s with a separator element between them this is whitespace-sensitive inline
 * markup that prettier and the Vue formatter disagree about, and three inline boxes for one
 * short line is three things the bidi algorithm can reorder independently. One string, one
 * node, one direction.
 *
 * The count moved because it is now somewhere to GO rather than something to read — the
 * whole point of the links row below — and printing it twice would be two places for one
 * number to be right.
 */
const metaLine = computed(() => changedLabel.value);

/**
 * The accent means "this one is running", which is a true and useful thing for a mark to
 * mean in a list of the owner's own tasks — unlike the attention queue, where a list of
 * problems must never contain the colour that means "fine".
 */
const mark = computed(() =>
  props.task.enabled
    ? { tile: 'bg-n-blue-3', icon: 'text-n-blue-10' }
    : { tile: 'bg-n-alpha-2', icon: 'text-n-slate-10' }
);

const base = computed(() =>
  props.accountId === null
    ? null
    : frontendURL(`accounts/${props.accountId}/waslo/tasks/${props.task.id}`)
);

/**
 * Edit lands on the SPINE, not on the name-and-trigger screen the composer opens a NEW
 * task with. On the fortieth visit the question is never "what is this task called" — the
 * blind reading named "the whole task visible at once" as the one thing the old card list
 * did better — and `resolveStep` provably cannot bounce a complete draft, which a saved
 * task is by construction.
 */
const to = computed(() =>
  base.value === null ? null : { path: base.value, query: { step: 'steps' } }
);

const arabicTo = computed(() =>
  base.value === null || !props.task.hasGraph
    ? null
    : { path: base.value, query: { step: 'arabic' } }
);

/**
 * The systems this task can reach — or the way to make its first one.
 *
 * `?return=` + `?returnId=` is the connection editor's own contract: it checks that name
 * against `router.hasRoute()` and against a `waslo_`-only pattern before it uses it, and
 * refuses a target it cannot build. Without the id it would have no task to come back to,
 * so both travel or neither does.
 *
 * ── EVERY LINK ON THIS ROW IS A PATH, AND NOT ONE IS A ROUTE NAME ────────────────────
 *
 * This one was `{ name: 'waslo_connection_new', … }` for about an hour, and the render
 * harness caught what that costs: `router-link` resolves its `to` inside `useLink`'s
 * computed, during setup, and a NAME the router does not carry throws there — the same
 * throw, in the same shape, that blanks the entire primary navigation from inside
 * `SidebarGroup`. It took the whole page down to a stack trace.
 *
 * In the real app the name is registered, so that particular throw was the harness's
 * router being smaller than the app's. The lesson is not about the harness. A path is
 * matched, not looked up: the worst a wrong one can do is 404 one navigation, which is a
 * bad link. A wrong name is a blank screen. On a row that exists to carry FOUR links out
 * to four other objects, that asymmetry decides it — so the target is spelled as a path,
 * and the route NAME travels in the query, where it is somebody else's job to validate it
 * and where they already do.
 */
const systemsTo = computed(() => {
  if (props.accountId === null) return null;
  if (props.task.connectionCount > 0) {
    return frontendURL(
      `accounts/${props.accountId}/waslo/settings/connections`
    );
  }
  return {
    path: frontendURL(
      `accounts/${props.accountId}/waslo/settings/connections/new`
    ),
    query: { return: 'waslo_task_edit', returnId: String(props.task.id) },
  };
});

const systemsLabel = computed(() =>
  props.task.connectionCount > 0
    ? connectionsLabel.value
    : t('WASLO.TASKS.LIST.LINKS.CONNECT')
);

/** The Forms tab of Approved messages — where a task's form is previewed and published. */
const formTo = computed(() =>
  props.accountId === null || !props.task.hasGraph
    ? null
    : {
        path: frontendURL(`accounts/${props.accountId}/waslo/templates`),
        query: { tab: 'forms' },
      }
);

/** The Menus tab of this page — the row that used to be in the sidebar. */
const menusTo = computed(() =>
  props.accountId === null
    ? null
    : {
        path: frontendURL(`accounts/${props.accountId}/waslo/tasks`),
        query: { tab: 'menus' },
      }
);

const onToggle = next => emit('toggle', next);
</script>

<template>
  <li
    class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center"
    :data-waslo-task="task.id"
    :data-waslo-task-enabled="task.enabled"
  >
    <span
      class="grid rounded-lg shrink-0 place-items-center size-9"
      :class="mark.tile"
      aria-hidden="true"
    >
      <span class="size-4 i-lucide-list-checks" :class="mark.icon" />
    </span>

    <div class="flex flex-col min-w-0 gap-1">
      <!--
        A `<bdi>` INSIDE A PLAIN PARAGRAPH, not `dir="auto"` ON the paragraph — and the
        difference is visible, not pedantic. `dir="auto"` on the `<p>` makes the whole
        BLOCK right-to-left for an Arabic title, so «وين وصل طلبي» flies to the far edge of
        a 700px row while the trigger line under it stays at the other one, and one row in
        four looks broken. `<bdi>` is an inline isolate that carries `dir="auto"` of its own:
        the name is laid out right-to-left inside its own box, and the box sits at the
        column's inline start with every other row. The page's direction decides where the
        row starts; the name's direction decides only how the name reads.
      -->
      <p
        class="mb-0 text-sm font-medium break-words text-n-slate-12"
        data-waslo-task-title
      >
        <bdi>{{ task.title }}</bdi>
      </p>
      <ClaudeChip v-if="suggestedByClaude" />
      <!-- Three nodes, never one string: the middle one is the bidi isolate. -->
      <p
        class="mb-0 text-sm break-words text-n-slate-11 line-clamp-2"
        dir="auto"
        data-waslo-task-trigger
      >
        <span>{{ startsWhen.before }}</span
        ><bdi>{{ startsWhen.value }}</bdi
        ><span>{{ startsWhen.after }}</span>
      </p>
      <p
        v-if="metaLine"
        class="mb-0 text-xs text-n-slate-10"
        data-waslo-task-meta
      >
        {{ metaLine }}
      </p>
      <div
        v-if="notOn.length"
        class="flex flex-wrap gap-1"
        data-waslo-task-not-on
      >
        <span
          v-for="item in notOn"
          :key="item.kind"
          class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
          :data-waslo-task-not-on-channel="item.kind"
        >
          {{ item.label }}
        </span>
      </div>
      <p
        v-if="usageLine"
        class="mb-0 text-xs text-n-slate-10"
        data-waslo-task-usage
      >
        {{ usageLine }}
      </p>

      <!--
        WHERE THIS TASK GOES. A `<nav>` labelled with the task's own title, so a screen
        reader announces which row's links these are without every link having to repeat
        the title inside a translated frame — which would be four more bidi isolates per
        row for a string nobody reads visually.

        `flex-wrap` and `gap-x-2`, never a `·` separator element between them: a separator
        is a fifth inline box the bidi algorithm can reorder on its own, and it lands in the
        wrong place the moment one of these labels is Arabic inside an English page. The gap
        IS the separator.
      -->
      <nav
        class="flex flex-wrap items-center pt-0.5 gap-x-3 gap-y-1"
        :aria-label="task.title"
        data-waslo-task-links
      >
        <router-link
          v-if="systemsTo"
          :to="systemsTo"
          class="text-xs font-medium text-n-blue-11 hover:underline"
          data-waslo-task-link="systems"
        >
          {{ systemsLabel }}
        </router-link>
        <router-link
          v-if="arabicTo"
          :to="arabicTo"
          class="text-xs font-medium text-n-blue-11 hover:underline"
          data-waslo-task-link="arabic"
        >
          {{ t('WASLO.TASKS.ARABIC.TITLE') }}
        </router-link>
        <router-link
          v-if="formTo"
          :to="formTo"
          class="text-xs font-medium text-n-blue-11 hover:underline"
          data-waslo-task-link="form"
        >
          {{ t('WASLO.TASKS.LIST.LINKS.FORM') }}
        </router-link>
        <router-link
          v-if="menusTo"
          :to="menusTo"
          class="text-xs font-medium text-n-blue-11 hover:underline"
          data-waslo-task-link="menus"
        >
          {{ t('WASLO.TASKS.LIST.LINKS.MENU') }}
        </router-link>
      </nav>

      <!-- F3.6: remove it, with the confirm in place under the row's words. -->
      <TaskRemove
        v-if="accountId !== null"
        compact
        :account-id="accountId"
        :task-id="task.id"
        @removed="id => emit('removed', id)"
      />
    </div>

    <!-- The same slot, in the same order, on every row. -->
    <div class="flex items-center gap-3 shrink-0 sm:ms-auto ms-0">
      <span class="text-sm text-n-slate-11" data-waslo-task-state>
        {{
          isSaving
            ? t('WASLO.TASKS.EDITOR.STATUS.SAVING')
            : task.enabled
              ? t('WASLO.TASKS.LIST.ON')
              : t('WASLO.TASKS.LIST.OFF')
        }}
      </span>
      <Switch
        :key="switchEpoch"
        :model-value="task.enabled"
        :aria-label="task.title"
        data-waslo-task-switch
        @update:model-value="onToggle"
      />
      <router-link
        v-if="to"
        :to="to"
        class="inline-flex items-center h-8 gap-1 px-3 text-sm font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-slate-4"
        data-waslo-task-edit
      >
        {{ t('WASLO.TASKS.LIST.EDIT') }}
      </router-link>
    </div>
  </li>
</template>
